import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture } from '@react-three/drei';
import * as THREE from 'three';

const EarthAndTides = () => {
  const earthMeshRef = useRef<THREE.Mesh>(null);
  const moonSystemRef = useRef<THREE.Group>(null);
  
  const earthMap = useTexture('/textures/earth.jpg');

  useFrame((state, delta) => {
    if (earthMeshRef.current && moonSystemRef.current) {
      // Earth day rotation
      earthMeshRef.current.rotation.y += delta * 0.5;
      
      // Moon orbit (and tidal bulge tracks it)
      moonSystemRef.current.rotation.y += delta * 0.2;
    }
  });

  return (
    <group>
      {/* Earth Surface */}
      <mesh ref={earthMeshRef}>
        <sphereGeometry args={[2, 64, 64]} />
        <meshStandardMaterial map={earthMap} roughness={0.7} />
      </mesh>

      {/* Moon and Tidal Bulge System */}
      <group ref={moonSystemRef}>
        {/* Tidal Bulge (Ellipsoid stretched along X axis) */}
        <mesh>
          <sphereGeometry args={[2.02, 64, 64]} />
          <meshPhysicalMaterial 
            color="#38BDF8" 
            transparent 
            opacity={0.4} 
            roughness={0.1} 
            transmission={0.5} 
            thickness={0.5} 
          />
          {/* Stretch X to 1.15, Y and Z to 1.05 to make a clear bulge */}
          <primitive object={new THREE.Vector3(1.15, 1.02, 1.02)} attach="scale" />
        </mesh>

        {/* Moon */}
        <mesh position={[7, 0, 0]}>
          <sphereGeometry args={[0.5, 32, 32]} />
          <meshStandardMaterial color="#A0A0A0" roughness={0.9} />
        </mesh>
      </group>
    </group>
  );
};

const Starfield = () => {
  const starsGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const count = 100;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 30;
      const theta = 2 * Math.PI * Math.random();
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geo;
  }, []);

  return (
    <points geometry={starsGeo}>
      <pointsMaterial color="#FFFFFF" size={0.15} sizeAttenuation={true} />
    </points>
  );
};

export const TidalBulge3D: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: '100%', height: Number(height) - 40, borderRadius: '8px', overflow: 'hidden' }}>
        <Canvas camera={{ position: [0, 5, 12], fov: 45 }}>
          <ambientLight intensity={0.2} />
          <directionalLight position={[10, 5, 5]} intensity={1.5} />
          
          <Starfield />
          <EarthAndTides />
          
          <OrbitControls enableZoom={true} enablePan={false} />
        </Canvas>
      </div>
      <div style={{ marginTop: '12px', fontSize: '13px', color: '#94A3B8', textAlign: 'center' }}>
        Two bulges = two daily high tides
      </div>
    </div>
  );
};
